// Fatigue dashboard mock data + small aggregate helpers.
// Ported verbatim from the prototype; computed scores are filled in at the bottom
// by initScores(). scores.ts only imports TYPES from here, so there is no runtime cycle.

import { calcCDI, calcFED, calcLRM, calcPLI, calcPSI } from './scores';

export type RoleType = 'white' | 'blue' | 'mixed';

export interface Shift {
  date: string;
  /** 'HH:MM' or null on a rest day */
  in: string | null;
  /** null while the shift is still in progress (or on a rest day) */
  out: string | null;
  /** km driven */
  drive: number;
}

export interface Region {
  name: string;
  color: string;
  groups: number[];
}

export interface Group {
  id: number;
  name: string;
  region: string;
}

/** Fields supplied by the mock data / onboarding. */
export interface RawWorker {
  init: string;
  name: string;
  group: number;
  role: string;
  roleType: RoleType;
  age: number;
  weight_kg: number;
  gender: 'M' | 'F';
  hrv: number;
  rhr: number;
  slp: number;
  hrv_b: number;
  rhr_b: number;
  slp_b: number;
  restHrs: number;
  consec: number;
  edaScore: number;
  assessScore: number;
  hr_work: number;
  hr_work_b: number;
  hrv_pre: number;
  hrv_post: number;
  hr_rec: number;
  hr_rec_b: number;
  shiftHrs: number;
  normalHrs: number;
  otHrs: number;
  driveKm: number;
  driveDays: number;
  wtrFlag: boolean;
  inWorkFed: number;
  outWorkFed: number;
  fed12: number[];
  slp7: number[];
  /** hours the HRV peak deviates from the personal baseline window (CDI input) */
  hrv_peak_offset: number;
  /** RMSE of today's RHR diurnal curve vs 28d baseline curve (CDI input) */
  rhr_curve_rmse: number;
  /** hours sleep onset has drifted from the habitual window (CDI input) */
  slp_onset_drift: number;
  /** sleep efficiency % (CDI modifier) */
  slp_efficiency: number;
  shifts: Shift[];
}

/** Scores cached on each worker by initScores(). */
export interface WorkerScores {
  fed: number;
  cdi: number;
  lrm: number;
  lrmDemand: number;
  lrmReady: number;
  pli: number;
  pliHRR: number;
  pliAuto: number;
  pliEDA: number;
  pliKcal: number;
  psi: number;
  psiAuto: number;
  psiCog: number;
  psiPat: number;
}

export type Worker = RawWorker & WorkerScores;

export const REGIONS: Record<string, Region> = {
  central: { name: 'Central', color: '#3987e5', groups: [0, 1, 2] },
  fleet: { name: 'Fleet & Logistics', color: '#eb6834', groups: [3, 4] },
  nl: { name: 'NL', color: '#1baf7a', groups: [5, 6] },
  nw: { name: 'NW', color: '#9085e9', groups: [7, 8] },
};

export const GROUPS: Group[] = [
  { id: 0, name: 'Dispatch', region: 'central' },
  { id: 1, name: 'Emergency Contact Centre', region: 'central' },
  { id: 2, name: 'Manager / Supervisor', region: 'central' },
  { id: 3, name: 'Driver', region: 'fleet' },
  { id: 4, name: 'Logistics Operative', region: 'fleet' },
  { id: 5, name: 'Energy Ops & Capital Delivery', region: 'nl' },
  { id: 6, name: 'Repair & Connections', region: 'nl' },
  { id: 7, name: 'Emergency & MOBS', region: 'nw' },
  { id: 8, name: 'Non-Management Staff', region: 'nw' },
];

// Typed as RawWorker so the nested literals are checked; scores are attached by initScores().
const RAW_WORKERS: Record<string, RawWorker> = {
  MC: {
    init: 'MC',
    name: 'Maria Chen',
    group: 0,
    role: 'Dispatcher',
    roleType: 'white',
    age: 34,
    weight_kg: 62,
    gender: 'F',
    hrv: 31,
    rhr: 74,
    slp: 4.9,
    hrv_b: 58,
    rhr_b: 66,
    slp_b: 8.1,
    restHrs: 10.5,
    consec: 4,
    edaScore: 72,
    assessScore: 14,
    hr_work: 128,
    hr_work_b: 108,
    hrv_pre: 38,
    hrv_post: 32,
    hr_rec: 24,
    hr_rec_b: 32,
    shiftHrs: 44,
    normalHrs: 40,
    otHrs: 4,
    driveKm: 0,
    driveDays: 0,
    wtrFlag: false,
    inWorkFed: 30,
    outWorkFed: 70,
    fed12: [40, 42, 44, 48, 52, 55, 58, 62, 65, 68, 71, 74],
    slp7: [6.8, 6.5, 6.2, 5.8, 5.4, 5.1, 4.9],
    hrv_peak_offset: 2.8,
    rhr_curve_rmse: 4.2,
    slp_onset_drift: 1.4,
    slp_efficiency: 80,
    shifts: [
      { date: '2026-10-09', in: '08:00', out: null, drive: 0 },
      { date: '2026-10-08', in: '08:00', out: '17:00', drive: 0 },
      { date: '2026-10-07', in: '08:00', out: '17:30', drive: 0 },
      { date: '2026-10-06', in: null, out: null, drive: 0 },
      { date: '2026-10-05', in: '08:00', out: '17:00', drive: 0 },
    ],
  },
  DK: {
    init: 'DK',
    name: 'Dan Kowalski',
    group: 0,
    role: 'Dispatch Controller',
    roleType: 'white',
    age: 41,
    weight_kg: 84,
    gender: 'M',
    hrv: 52,
    rhr: 62,
    slp: 7.0,
    hrv_b: 56,
    rhr_b: 64,
    slp_b: 7.2,
    restHrs: 13.5,
    consec: 2,
    edaScore: 28,
    assessScore: 5,
    hr_work: 106,
    hr_work_b: 100,
    hrv_pre: 54,
    hrv_post: 52,
    hr_rec: 32,
    hr_rec_b: 32,
    shiftHrs: 36,
    normalHrs: 40,
    otHrs: 0,
    driveKm: 60,
    driveDays: 2,
    wtrFlag: false,
    inWorkFed: 20,
    outWorkFed: 80,
    fed12: [36, 35, 34, 33, 32, 32, 32, 32, 32, 32, 32, 32],
    slp7: [7.0, 7.1, 6.9, 7.0, 7.0, 7.1, 7.0],
    hrv_peak_offset: 0.4,
    rhr_curve_rmse: 1.2,
    slp_onset_drift: 0.2,
    slp_efficiency: 92,
    shifts: [
      { date: '2026-10-09', in: '08:00', out: null, drive: 30 },
      { date: '2026-10-08', in: '08:00', out: '16:30', drive: 30 },
      { date: '2026-10-07', in: null, out: null, drive: 0 },
      { date: '2026-10-06', in: '08:00', out: '16:00', drive: 30 },
      { date: '2026-10-05', in: null, out: null, drive: 0 },
    ],
  },
  SW: {
    init: 'SW',
    name: 'Sarah Willis',
    group: 1,
    role: 'ECC Operator',
    roleType: 'white',
    age: 29,
    weight_kg: 58,
    gender: 'F',
    hrv: 38,
    rhr: 68,
    slp: 5.8,
    hrv_b: 60,
    rhr_b: 66,
    slp_b: 7.2,
    restHrs: 12.0,
    consec: 3,
    edaScore: 55,
    assessScore: 10,
    hr_work: 118,
    hr_work_b: 102,
    hrv_pre: 44,
    hrv_post: 40,
    hr_rec: 30,
    hr_rec_b: 28,
    shiftHrs: 36,
    normalHrs: 36,
    otHrs: 0,
    driveKm: 0,
    driveDays: 0,
    wtrFlag: false,
    inWorkFed: 45,
    outWorkFed: 55,
    fed12: [50, 52, 54, 56, 58, 60, 60, 62, 64, 64, 65, 65],
    slp7: [6.2, 6.0, 5.8, 5.9, 5.7, 5.8, 5.8],
    hrv_peak_offset: 3.5,
    rhr_curve_rmse: 5.8,
    slp_onset_drift: 2.6,
    slp_efficiency: 76,
    shifts: [
      { date: '2026-10-09', in: '22:00', out: null, drive: 0 },
      { date: '2026-10-08', in: '22:00', out: '06:00', drive: 0 },
      { date: '2026-10-07', in: null, out: null, drive: 0 },
      { date: '2026-10-06', in: '22:00', out: '06:00', drive: 0 },
      { date: '2026-10-05', in: null, out: null, drive: 0 },
    ],
  },
  NR: {
    init: 'NR',
    name: 'Nina Roberts',
    group: 1,
    role: 'ECC Supervisor',
    roleType: 'white',
    age: 38,
    weight_kg: 65,
    gender: 'F',
    hrv: 45,
    rhr: 64,
    slp: 6.6,
    hrv_b: 57,
    rhr_b: 66,
    slp_b: 7.0,
    restHrs: 12.0,
    consec: 3,
    edaScore: 40,
    assessScore: 7,
    hr_work: 114,
    hr_work_b: 106,
    hrv_pre: 48,
    hrv_post: 44,
    hr_rec: 28,
    hr_rec_b: 30,
    shiftHrs: 40,
    normalHrs: 40,
    otHrs: 0,
    driveKm: 0,
    driveDays: 0,
    wtrFlag: false,
    inWorkFed: 28,
    outWorkFed: 72,
    fed12: [44, 44, 44, 44, 45, 45, 45, 45, 45, 45, 45, 45],
    slp7: [6.5, 6.6, 6.6, 6.7, 6.5, 6.6, 6.6],
    hrv_peak_offset: 0.9,
    rhr_curve_rmse: 1.8,
    slp_onset_drift: 0.4,
    slp_efficiency: 90,
    shifts: [
      { date: '2026-10-09', in: '08:00', out: null, drive: 0 },
      { date: '2026-10-08', in: '08:00', out: '17:00', drive: 0 },
      { date: '2026-10-07', in: '08:00', out: '17:30', drive: 0 },
      { date: '2026-10-06', in: null, out: null, drive: 0 },
      { date: '2026-10-05', in: '08:00', out: '17:00', drive: 0 },
    ],
  },
  LB: {
    init: 'LB',
    name: 'Liam Brooks',
    group: 2,
    role: 'Operations Manager',
    roleType: 'mixed',
    age: 45,
    weight_kg: 80,
    gender: 'M',
    hrv: 57,
    rhr: 60,
    slp: 7.2,
    hrv_b: 62,
    rhr_b: 64,
    slp_b: 7.4,
    restHrs: 14.0,
    consec: 2,
    edaScore: 22,
    assessScore: 4,
    hr_work: 108,
    hr_work_b: 104,
    hrv_pre: 58,
    hrv_post: 56,
    hr_rec: 34,
    hr_rec_b: 34,
    shiftHrs: 38,
    normalHrs: 40,
    otHrs: 0,
    driveKm: 0,
    driveDays: 0,
    wtrFlag: false,
    inWorkFed: 35,
    outWorkFed: 65,
    fed12: [35, 34, 32, 30, 30, 29, 28, 28, 28, 28, 28, 28],
    slp7: [7.1, 7.3, 7.2, 7.4, 7.2, 7.1, 7.2],
    hrv_peak_offset: 0.3,
    rhr_curve_rmse: 0.8,
    slp_onset_drift: 0.1,
    slp_efficiency: 94,
    shifts: [
      { date: '2026-10-09', in: '07:00', out: null, drive: 0 },
      { date: '2026-10-08', in: null, out: null, drive: 0 },
      { date: '2026-10-07', in: '07:00', out: '16:00', drive: 0 },
      { date: '2026-10-06', in: null, out: null, drive: 0 },
      { date: '2026-10-05', in: null, out: null, drive: 0 },
    ],
  },
  JH: {
    init: 'JH',
    name: 'James Hartley',
    group: 3,
    role: 'HGV Driver',
    roleType: 'blue',
    age: 39,
    weight_kg: 91,
    gender: 'M',
    hrv: 30,
    rhr: 72,
    slp: 5.1,
    hrv_b: 52,
    rhr_b: 62,
    slp_b: 7.8,
    restHrs: 8.5,
    consec: 6,
    edaScore: 88,
    assessScore: 16,
    hr_work: 142,
    hr_work_b: 118,
    hrv_pre: 35,
    hrv_post: 28,
    hr_rec: 18,
    hr_rec_b: 28,
    shiftHrs: 52,
    normalHrs: 40,
    otHrs: 12,
    driveKm: 640,
    driveDays: 5,
    wtrFlag: true,
    inWorkFed: 58,
    outWorkFed: 42,
    fed12: [55, 58, 62, 65, 68, 70, 74, 76, 78, 80, 81, 82],
    slp7: [5.8, 5.5, 5.2, 5.1, 4.9, 5.3, 5.1],
    hrv_peak_offset: 2.2,
    rhr_curve_rmse: 4.8,
    slp_onset_drift: 1.8,
    slp_efficiency: 79,
    shifts: [
      { date: '2026-10-09', in: '06:00', out: null, drive: 320 },
      { date: '2026-10-08', in: '06:00', out: '18:30', drive: 290 },
      { date: '2026-10-07', in: '06:00', out: '19:00', drive: 310 },
      { date: '2026-10-06', in: null, out: null, drive: 0 },
      { date: '2026-10-05', in: '06:00', out: '19:30', drive: 280 },
    ],
  },
  RP: {
    init: 'RP',
    name: 'Raj Patel',
    group: 3,
    role: 'HGV Driver',
    roleType: 'blue',
    age: 44,
    weight_kg: 88,
    gender: 'M',
    hrv: 28,
    rhr: 76,
    slp: 4.4,
    hrv_b: 55,
    rhr_b: 65,
    slp_b: 7.5,
    restHrs: 7.2,
    consec: 7,
    edaScore: 92,
    assessScore: 18,
    hr_work: 148,
    hr_work_b: 120,
    hrv_pre: 32,
    hrv_post: 22,
    hr_rec: 12,
    hr_rec_b: 26,
    shiftHrs: 56,
    normalHrs: 40,
    otHrs: 16,
    driveKm: 840,
    driveDays: 6,
    wtrFlag: true,
    inWorkFed: 70,
    outWorkFed: 30,
    fed12: [60, 64, 68, 70, 72, 76, 78, 80, 82, 84, 86, 88],
    slp7: [5.2, 4.9, 4.6, 4.5, 4.3, 4.5, 4.4],
    hrv_peak_offset: 3.8,
    rhr_curve_rmse: 7.2,
    slp_onset_drift: 3.1,
    slp_efficiency: 71,
    shifts: [
      { date: '2026-10-09', in: '05:00', out: null, drive: 420 },
      { date: '2026-10-08', in: '05:00', out: '20:00', drive: 380 },
      { date: '2026-10-07', in: '05:00', out: '19:30', drive: 360 },
      { date: '2026-10-06', in: '05:00', out: '20:30', drive: 410 },
      { date: '2026-10-05', in: '05:00', out: '19:00', drive: 340 },
    ],
  },
  CJ: {
    init: 'CJ',
    name: 'Clara Johansson',
    group: 3,
    role: 'LGV Driver',
    roleType: 'blue',
    age: 31,
    weight_kg: 67,
    gender: 'F',
    hrv: 33,
    rhr: 71,
    slp: 5.3,
    hrv_b: 53,
    rhr_b: 63,
    slp_b: 7.6,
    restHrs: 9.0,
    consec: 5,
    edaScore: 80,
    assessScore: 15,
    hr_work: 138,
    hr_work_b: 114,
    hrv_pre: 36,
    hrv_post: 30,
    hr_rec: 16,
    hr_rec_b: 26,
    shiftHrs: 50,
    normalHrs: 40,
    otHrs: 10,
    driveKm: 580,
    driveDays: 5,
    wtrFlag: true,
    inWorkFed: 60,
    outWorkFed: 40,
    fed12: [52, 54, 56, 60, 64, 66, 68, 70, 72, 74, 76, 78],
    slp7: [5.6, 5.4, 5.2, 5.3, 5.1, 5.3, 5.3],
    hrv_peak_offset: 2.0,
    rhr_curve_rmse: 4.1,
    slp_onset_drift: 1.6,
    slp_efficiency: 81,
    shifts: [
      { date: '2026-10-09', in: '06:00', out: null, drive: 290 },
      { date: '2026-10-08', in: '06:00', out: '18:30', drive: 270 },
      { date: '2026-10-07', in: '06:00', out: '19:00', drive: 290 },
      { date: '2026-10-06', in: null, out: null, drive: 0 },
      { date: '2026-10-05', in: '06:00', out: '19:30', drive: 260 },
    ],
  },
  KO: {
    init: 'KO',
    name: 'Kenji Okafor',
    group: 4,
    role: 'Logistics Operative',
    roleType: 'blue',
    age: 27,
    weight_kg: 74,
    gender: 'M',
    hrv: 48,
    rhr: 64,
    slp: 6.8,
    hrv_b: 55,
    rhr_b: 65,
    slp_b: 7.0,
    restHrs: 13.0,
    consec: 3,
    edaScore: 38,
    assessScore: 7,
    hr_work: 112,
    hr_work_b: 105,
    hrv_pre: 50,
    hrv_post: 46,
    hr_rec: 28,
    hr_rec_b: 30,
    shiftHrs: 40,
    normalHrs: 40,
    otHrs: 0,
    driveKm: 0,
    driveDays: 0,
    wtrFlag: false,
    inWorkFed: 25,
    outWorkFed: 75,
    fed12: [45, 44, 43, 43, 42, 42, 42, 42, 42, 42, 42, 42],
    slp7: [6.6, 6.7, 6.8, 6.9, 6.8, 6.8, 6.8],
    hrv_peak_offset: 0.5,
    rhr_curve_rmse: 1.4,
    slp_onset_drift: 0.3,
    slp_efficiency: 91,
    shifts: [
      { date: '2026-10-09', in: '09:00', out: null, drive: 0 },
      { date: '2026-10-08', in: '09:00', out: '18:00', drive: 0 },
      { date: '2026-10-07', in: '09:00', out: '17:30', drive: 0 },
      { date: '2026-10-06', in: null, out: null, drive: 0 },
      { date: '2026-10-05', in: '09:00', out: '17:00', drive: 0 },
    ],
  },
  PL: {
    init: 'PL',
    name: 'Priya Lal',
    group: 4,
    role: 'Logistics Operative',
    roleType: 'blue',
    age: 33,
    weight_kg: 60,
    gender: 'F',
    hrv: 44,
    rhr: 65,
    slp: 6.5,
    hrv_b: 56,
    rhr_b: 67,
    slp_b: 7.1,
    restHrs: 12.5,
    consec: 3,
    edaScore: 44,
    assessScore: 8,
    hr_work: 116,
    hr_work_b: 108,
    hrv_pre: 46,
    hrv_post: 44,
    hr_rec: 28,
    hr_rec_b: 30,
    shiftHrs: 42,
    normalHrs: 40,
    otHrs: 2,
    driveKm: 0,
    driveDays: 0,
    wtrFlag: false,
    inWorkFed: 42,
    outWorkFed: 58,
    fed12: [42, 43, 44, 45, 46, 47, 47, 47, 48, 48, 48, 48],
    slp7: [6.4, 6.5, 6.4, 6.6, 6.5, 6.5, 6.5],
    hrv_peak_offset: 0.8,
    rhr_curve_rmse: 1.9,
    slp_onset_drift: 0.5,
    slp_efficiency: 89,
    shifts: [
      { date: '2026-10-09', in: '07:00', out: null, drive: 0 },
      { date: '2026-10-08', in: '07:00', out: '17:30', drive: 0 },
      { date: '2026-10-07', in: null, out: null, drive: 0 },
      { date: '2026-10-06', in: '07:00', out: '17:00', drive: 0 },
      { date: '2026-10-05', in: null, out: null, drive: 0 },
    ],
  },
  TN: {
    init: 'TN',
    name: 'Tara Nguyen',
    group: 5,
    role: 'Energy Ops Technician',
    roleType: 'blue',
    age: 36,
    weight_kg: 63,
    gender: 'F',
    hrv: 35,
    rhr: 70,
    slp: 5.5,
    hrv_b: 54,
    rhr_b: 63,
    slp_b: 7.6,
    restHrs: 9.5,
    consec: 5,
    edaScore: 68,
    assessScore: 13,
    hr_work: 136,
    hr_work_b: 112,
    hrv_pre: 40,
    hrv_post: 34,
    hr_rec: 20,
    hr_rec_b: 28,
    shiftHrs: 48,
    normalHrs: 40,
    otHrs: 8,
    driveKm: 520,
    driveDays: 4,
    wtrFlag: false,
    inWorkFed: 55,
    outWorkFed: 45,
    fed12: [48, 50, 54, 56, 58, 60, 62, 64, 66, 68, 69, 70],
    slp7: [6.2, 5.9, 5.7, 5.6, 5.4, 5.5, 5.5],
    hrv_peak_offset: 1.8,
    rhr_curve_rmse: 3.6,
    slp_onset_drift: 1.2,
    slp_efficiency: 83,
    shifts: [
      { date: '2026-10-09', in: '06:00', out: null, drive: 260 },
      { date: '2026-10-08', in: '06:00', out: '18:00', drive: 240 },
      { date: '2026-10-07', in: null, out: null, drive: 0 },
      { date: '2026-10-06', in: '06:00', out: '19:00', drive: 260 },
      { date: '2026-10-05', in: '06:00', out: '18:30', drive: 230 },
    ],
  },
  FO: {
    init: 'FO',
    name: 'Fatima Osei',
    group: 5,
    role: 'Capital Delivery Lead',
    roleType: 'mixed',
    age: 42,
    weight_kg: 69,
    gender: 'F',
    hrv: 26,
    rhr: 78,
    slp: 4.2,
    hrv_b: 50,
    rhr_b: 60,
    slp_b: 7.4,
    restHrs: 6.5,
    consec: 8,
    edaScore: 95,
    assessScore: 19,
    hr_work: 152,
    hr_work_b: 116,
    hrv_pre: 30,
    hrv_post: 20,
    hr_rec: 10,
    hr_rec_b: 24,
    shiftHrs: 60,
    normalHrs: 40,
    otHrs: 20,
    driveKm: 980,
    driveDays: 7,
    wtrFlag: true,
    inWorkFed: 72,
    outWorkFed: 28,
    fed12: [62, 65, 68, 70, 74, 78, 80, 82, 84, 87, 89, 91],
    slp7: [5.0, 4.8, 4.5, 4.3, 4.1, 4.3, 4.2],
    hrv_peak_offset: 4.5,
    rhr_curve_rmse: 8.6,
    slp_onset_drift: 3.8,
    slp_efficiency: 68,
    shifts: [
      { date: '2026-10-09', in: '05:00', out: null, drive: 490 },
      { date: '2026-10-08', in: '05:00', out: '20:30', drive: 460 },
      { date: '2026-10-07', in: '05:00', out: '21:00', drive: 480 },
      { date: '2026-10-06', in: '05:00', out: '20:00', drive: 430 },
      { date: '2026-10-05', in: '05:00', out: '21:30', drive: 450 },
    ],
  },
  BM: {
    init: 'BM',
    name: 'Ben Murphy',
    group: 6,
    role: 'Repair & Connections Tech',
    roleType: 'blue',
    age: 37,
    weight_kg: 82,
    gender: 'M',
    hrv: 50,
    rhr: 63,
    slp: 6.9,
    hrv_b: 58,
    rhr_b: 65,
    slp_b: 7.3,
    restHrs: 13.0,
    consec: 2,
    edaScore: 30,
    assessScore: 5,
    hr_work: 110,
    hr_work_b: 104,
    hrv_pre: 52,
    hrv_post: 50,
    hr_rec: 32,
    hr_rec_b: 32,
    shiftHrs: 38,
    normalHrs: 40,
    otHrs: 0,
    driveKm: 80,
    driveDays: 2,
    wtrFlag: false,
    inWorkFed: 22,
    outWorkFed: 78,
    fed12: [38, 37, 37, 36, 36, 35, 35, 35, 35, 35, 35, 35],
    slp7: [6.8, 6.9, 7.0, 6.9, 6.8, 6.9, 6.9],
    hrv_peak_offset: 0.6,
    rhr_curve_rmse: 1.5,
    slp_onset_drift: 0.3,
    slp_efficiency: 92,
    shifts: [
      { date: '2026-10-09', in: '08:00', out: null, drive: 40 },
      { date: '2026-10-08', in: '08:00', out: '16:30', drive: 40 },
      { date: '2026-10-07', in: null, out: null, drive: 0 },
      { date: '2026-10-06', in: '08:00', out: '16:00', drive: 40 },
      { date: '2026-10-05', in: null, out: null, drive: 0 },
    ],
  },
  ET: {
    init: 'ET',
    name: 'Ethan Torres',
    group: 6,
    role: 'Connections Engineer',
    roleType: 'mixed',
    age: 30,
    weight_kg: 77,
    gender: 'M',
    hrv: 36,
    rhr: 69,
    slp: 5.6,
    hrv_b: 55,
    rhr_b: 65,
    slp_b: 7.3,
    restHrs: 10.0,
    consec: 4,
    edaScore: 62,
    assessScore: 12,
    hr_work: 132,
    hr_work_b: 110,
    hrv_pre: 40,
    hrv_post: 35,
    hr_rec: 22,
    hr_rec_b: 28,
    shiftHrs: 46,
    normalHrs: 40,
    otHrs: 6,
    driveKm: 440,
    driveDays: 4,
    wtrFlag: false,
    inWorkFed: 50,
    outWorkFed: 50,
    fed12: [45, 47, 50, 52, 55, 58, 60, 62, 64, 65, 67, 68],
    slp7: [6.0, 5.8, 5.7, 5.6, 5.5, 5.6, 5.6],
    hrv_peak_offset: 1.6,
    rhr_curve_rmse: 3.2,
    slp_onset_drift: 1.0,
    slp_efficiency: 84,
    shifts: [
      { date: '2026-10-09', in: '06:00', out: null, drive: 220 },
      { date: '2026-10-08', in: '06:00', out: '18:00', drive: 210 },
      { date: '2026-10-07', in: null, out: null, drive: 0 },
      { date: '2026-10-06', in: '06:00', out: '18:30', drive: 220 },
      { date: '2026-10-05', in: '06:00', out: '18:00', drive: 200 },
    ],
  },
  YA: {
    init: 'YA',
    name: 'Yusuf Adebayo',
    group: 7,
    role: 'MOBS Operative',
    roleType: 'blue',
    age: 35,
    weight_kg: 86,
    gender: 'M',
    hrv: 29,
    rhr: 75,
    slp: 4.7,
    hrv_b: 54,
    rhr_b: 64,
    slp_b: 7.7,
    restHrs: 8.0,
    consec: 6,
    edaScore: 90,
    assessScore: 17,
    hr_work: 145,
    hr_work_b: 119,
    hrv_pre: 33,
    hrv_post: 25,
    hr_rec: 14,
    hr_rec_b: 27,
    shiftHrs: 54,
    normalHrs: 40,
    otHrs: 14,
    driveKm: 720,
    driveDays: 5,
    wtrFlag: true,
    inWorkFed: 65,
    outWorkFed: 35,
    fed12: [56, 58, 62, 66, 68, 70, 74, 76, 78, 80, 83, 85],
    slp7: [5.4, 5.1, 4.9, 4.8, 4.6, 4.8, 4.7],
    hrv_peak_offset: 3.2,
    rhr_curve_rmse: 6.4,
    slp_onset_drift: 2.4,
    slp_efficiency: 74,
    shifts: [
      { date: '2026-10-09', in: '05:30', out: null, drive: 360 },
      { date: '2026-10-08', in: '05:30', out: '19:30', drive: 330 },
      { date: '2026-10-07', in: '05:30', out: '20:00', drive: 350 },
      { date: '2026-10-06', in: '05:30', out: '19:00', drive: 320 },
      { date: '2026-10-05', in: '05:30', out: '20:30', drive: 300 },
    ],
  },
  AW: {
    init: 'AW',
    name: 'Alex Walker',
    group: 7,
    role: 'Emergency Response',
    roleType: 'mixed',
    age: 28,
    weight_kg: 75,
    gender: 'M',
    hrv: 42,
    rhr: 66,
    slp: 6.2,
    hrv_b: 58,
    rhr_b: 68,
    slp_b: 7.0,
    restHrs: 11.5,
    consec: 4,
    edaScore: 50,
    assessScore: 9,
    hr_work: 122,
    hr_work_b: 106,
    hrv_pre: 46,
    hrv_post: 42,
    hr_rec: 26,
    hr_rec_b: 30,
    shiftHrs: 44,
    normalHrs: 40,
    otHrs: 4,
    driveKm: 180,
    driveDays: 3,
    wtrFlag: false,
    inWorkFed: 40,
    outWorkFed: 60,
    fed12: [42, 44, 46, 48, 50, 52, 53, 54, 55, 55, 55, 55],
    slp7: [6.8, 6.5, 6.3, 6.1, 6.2, 6.1, 6.2],
    hrv_peak_offset: 2.6,
    rhr_curve_rmse: 5.0,
    slp_onset_drift: 2.0,
    slp_efficiency: 78,
    shifts: [
      { date: '2026-10-09', in: '22:00', out: null, drive: 60 },
      { date: '2026-10-08', in: '22:00', out: '07:00', drive: 60 },
      { date: '2026-10-07', in: null, out: null, drive: 0 },
      { date: '2026-10-06', in: '22:00', out: '07:00', drive: 60 },
      { date: '2026-10-05', in: null, out: null, drive: 0 },
    ],
  },
  KP: {
    init: 'KP',
    name: 'Kim Park',
    group: 8,
    role: 'Non-Management Staff',
    roleType: 'white',
    age: 26,
    weight_kg: 57,
    gender: 'F',
    hrv: 53,
    rhr: 61,
    slp: 7.1,
    hrv_b: 60,
    rhr_b: 65,
    slp_b: 7.3,
    restHrs: 13.5,
    consec: 2,
    edaScore: 25,
    assessScore: 4,
    hr_work: 107,
    hr_work_b: 102,
    hrv_pre: 55,
    hrv_post: 53,
    hr_rec: 33,
    hr_rec_b: 33,
    shiftHrs: 37,
    normalHrs: 40,
    otHrs: 0,
    driveKm: 0,
    driveDays: 0,
    wtrFlag: false,
    inWorkFed: 18,
    outWorkFed: 82,
    fed12: [34, 33, 32, 31, 31, 30, 30, 30, 30, 30, 30, 30],
    slp7: [7.0, 7.1, 7.1, 7.2, 7.0, 7.1, 7.1],
    hrv_peak_offset: 0.2,
    rhr_curve_rmse: 0.9,
    slp_onset_drift: 0.1,
    slp_efficiency: 95,
    shifts: [
      { date: '2026-10-09', in: '08:30', out: null, drive: 0 },
      { date: '2026-10-08', in: '08:30', out: '16:30', drive: 0 },
      { date: '2026-10-07', in: null, out: null, drive: 0 },
      { date: '2026-10-06', in: '08:30', out: '16:30', drive: 0 },
      { date: '2026-10-05', in: null, out: null, drive: 0 },
    ],
  },
};

/** Compute and cache FED/CDI/LRM/PLI/PSI on each worker, then anchor fed12 to the computed FED. */
function initScores(): void {
  Object.values(RAW_WORKERS).forEach((raw) => {
    const w = raw as Worker;
    w.fed = calcFED(w);
    w.cdi = calcCDI(w);
    const lrm = calcLRM(w);
    w.lrm = lrm.lrm;
    w.lrmDemand = lrm.demand;
    w.lrmReady = lrm.readiness;
    const pliResult = calcPLI(w);
    w.pli = pliResult.pli;
    w.pliHRR = pliResult.hrrPct;
    w.pliAuto = pliResult.autoScore;
    w.pliEDA = pliResult.edaScore;
    w.pliKcal = pliResult.kcal;
    const psiResult = calcPSI(w);
    w.psi = psiResult.psi;
    w.psiAuto = psiResult.autoScore;
    w.psiCog = psiResult.cogScore;
    w.psiPat = psiResult.patternScore;
  });
  // Keep the trend shape but anchor the last point to the computed FED.
  Object.values(RAW_WORKERS).forEach((raw) => {
    const w = raw as Worker;
    if (w.fed12 && w.fed12.length) {
      const diff = w.fed - w.fed12[w.fed12.length - 1];
      w.fed12 = w.fed12.map((v) => Math.max(0, Math.min(100, v + diff)));
    }
  });
}

initScores();

export const WORKERS = RAW_WORKERS as Record<string, Worker>;

// ── Helpers ──────────────────────────────────────────────────────────────────
export const ws = (): Worker[] => Object.values(WORKERS);
export const wArr = (): Worker[] => ws();

/** 0 = Ready, 1 = Monitor, 2 = Not Ready (>=2 of: HRV -20%, RHR +8%, sleep -20% vs baseline). */
export function readiness(w: Worker): 0 | 1 | 2 {
  const dHRV = ((w.hrv - w.hrv_b) / w.hrv_b) * 100;
  const dRHR = ((w.rhr - w.rhr_b) / w.rhr_b) * 100;
  const dSLP = ((w.slp - w.slp_b) / w.slp_b) * 100;
  let f = 0;
  if (dHRV <= -20) f++;
  if (dRHR >= 8) f++;
  if (dSLP <= -20) f++;
  return f >= 2 ? 2 : f === 1 ? 1 : 0;
}
export const rdyLabel = (r: number): string => ['Ready', 'Monitor', 'Not Ready'][r];
export const sign = (v: number): string => (v > 0 ? '+' : '');
export const pct = (a: number, b: number): number => (b ? Math.round(((a - b) / b) * 100) : 0);

/** Individual Shift Fatigue Index (EDA 35% + HR elevation 35% + self-assessment 30%). */
export function isfi(w: Worker): number {
  const hrElev = w.hr_work_b > 0 ? Math.max(0, Math.min(((w.hr_work - w.hr_work_b) / w.hr_work_b) * 100, 100)) : 0;
  const assess = Math.round((w.assessScore / 20) * 100);
  return Math.round(Math.min(w.edaScore, 100) * 0.35 + hrElev * 0.35 + assess * 0.3);
}

export function workersInRegion(rid: string): Worker[] {
  const gids = REGIONS[rid].groups;
  return wArr().filter((w) => gids.includes(w.group));
}
export const workersInGroup = (gid: number): Worker[] => wArr().filter((w) => w.group === gid);
/** [regionId, region] for the worker's group (every group belongs to a region). */
export const regionOf = (w: Worker): [string, Region] =>
  Object.entries(REGIONS).find(([, r]) => r.groups.includes(w.group)) as [string, Region];

export const avgFED = (arr: Worker[]): number =>
  arr.length ? Math.round(arr.reduce((a, w) => a + w.fed, 0) / arr.length) : 0;
export const wtrBreaches = (arr: Worker[]): number => arr.filter((w) => w.wtrFlag).length;
export const notReadyCount = (arr: Worker[]): number => arr.filter((w) => readiness(w) === 2).length;
export const highFEDCount = (arr: Worker[]): number => arr.filter((w) => w.fed >= 80).length;

export const onShift = (w: Worker): boolean => w.shifts.some((s) => s.out === null);
